import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {useAccessToken, useCommerceApi, useProductSearch} from '@salesforce/commerce-sdk-react';
import {useQuery} from '@tanstack/react-query';

import {INVENTORY_POLICY} from '@amblique/king-living/app/constants/inventory';
import {chunk, GET_PRODUCTS_PAGE_SIZE, GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {useShopperInventoryIds} from '@amblique/king-living/app/hooks/use-shopper-inventory-ids';
import {
  collectReplacementFinishedGoodIdsFromProducts,
  collectUnresolvedMasterIds,
  mergeCurrentProductIntoMasters,
  pickInventoryRecordForListIds,
  resolveEntityAts,
  selectWantItSoonerTiles,
} from '@amblique/king-living/app/utils/inventory-utils';
import {IN_STOCK_SENTINEL_ATS} from '@amblique/king-living/app/pages/checkout/util/payment-amount-utils';

// productSearch: page size and max pages (cache-friendly: page 1 keyed by useProductSearch params).
const SEARCH_PAGE_SIZE = 200;
const SEARCH_MAX_PAGES = 5;

const searchParams = (categoryId, offset = 0) => ({
  refine: [`cgid=${categoryId}`],
  limit: SEARCH_PAGE_SIZE,
  offset,
});

/**
 * Paginated productSearch: page 1 via useProductSearch (React Query cache), remaining pages in parallel.
 * isComplete is true only after all pages are fetched so getProducts runs once on the final ID list.
 *
 * Retained for category productSearch (Choose Your Layout + range carousel).
 * Pass categoryId=undefined to leave this idle.
 */
const usePaginatedProductSearch = (categoryId) => {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();

  // Refs keep the latest values accessible inside effects without being deps.
  // Both api and getTokenWhenReady can have unstable references across renders
  // (new function/object identity each render), which would cause infinite loops
  // if included in useEffect deps. Refs solve this without stale-closure risk.
  const apiRef = useRef(api);
  const getTokenRef = useRef(getTokenWhenReady);

  apiRef.current = api;
  getTokenRef.current = getTokenWhenReady;

  const [extraHits, setExtraHits] = useState([]);
  const [isExtraLoading, setIsExtraLoading] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const {data: firstPageResult, isLoading: isFirstPageLoading} = useProductSearch(
    {parameters: searchParams(categoryId, 0)},
    {enabled: !!categoryId}
  );

  const firstPageHits = firstPageResult?.hits ?? [];
  const total = firstPageResult?.total ?? 0;

  useEffect(() => {
    setIsComplete(false);

    const totalPages = Math.ceil(total / SEARCH_PAGE_SIZE);
    const cappedPages = Math.min(totalPages, SEARCH_MAX_PAGES);
    const remainingPages = cappedPages - 1;

    if (!categoryId || !firstPageResult || remainingPages <= 0) {
      setExtraHits([]);
      setIsExtraLoading(false);
      setSearchError(null);
      if (firstPageResult) setIsComplete(true);

      return;
    }

    setSearchError(null);

    let cancelled = false;

    const fetchRemainingPages = async () => {
      setIsExtraLoading(true);
      const offsets = Array.from({length: remainingPages}, (_, i) => (i + 1) * SEARCH_PAGE_SIZE);

      try {
        const token = await getTokenRef.current();
        const results = await Promise.all(
          offsets.map((offset) => apiRef.current.shopperSearch.productSearch({parameters: searchParams(categoryId, offset), headers: {Authorization: `Bearer ${token}`}}).then((res) => res?.hits ?? []))
        );

        if (!cancelled) {
          setExtraHits(results.flat());
          setIsComplete(true);
        }
      } catch (err) {
        if (!cancelled) {
          setExtraHits([]);
          setIsComplete(true);
          setSearchError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (!cancelled) setIsExtraLoading(false);
      }
    };

    fetchRemainingPages();

    return () => {
      cancelled = true;
    };
  }, [categoryId, total]);

  return {
    hits: [...firstPageHits, ...extraHits],
    total,
    isLoading: isFirstPageLoading || isExtraLoading,
    isComplete: isComplete && !isFirstPageLoading,
    error: searchError,
  };
};

/**
 * Fetches products by ID in chunks via React Query.
 * Same product IDs reuse the cached response; no duplicate network calls across remounts.
 *
 * @param {string[]} productIds
 * @param {string|null|undefined} inventoryIdsParam
 * @param {string[]} [expandExtra] - extra SCAPI expand values (e.g. `links`)
 */
const usePaginatedProducts = (productIds, inventoryIdsParam, expandExtra = []) => {
  const api = useCommerceApi();
  const {getTokenWhenReady} = useAccessToken();
  const idsKey = productIds.join(',');
  const invKey = inventoryIdsParam ?? '';
  const expandKey = (expandExtra || []).join(',');
  const expand = Array.from(new Set([...(GET_PRODUCTS_PARAMS.expand || []), ...(expandExtra || [])]));

  const {data, isLoading, isFetched, error} = useQuery({
    queryKey: ['king-living/getProducts', idsKey, invKey, expandKey],
    queryFn: async () => {
      if (productIds.length === 0) return [];
      const token = await getTokenWhenReady();
      const chunks = chunk(productIds, GET_PRODUCTS_PAGE_SIZE);
      const results = await Promise.all(
        chunks.map((ids) => api.shopperProducts
          .getProducts({
            parameters: {
              ids: ids.join(','),
              ...GET_PRODUCTS_PARAMS,
              expand,
              ...(inventoryIdsParam ? {inventoryIds: inventoryIdsParam} : {}),
            },
            headers: {Authorization: `Bearer ${token}`},
          })
          .then((res) => res?.data ?? []))
      );

      return results.flat();
    },
    enabled: productIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  return {data: {data: data ?? []}, isLoading, isFetched, error: error ?? null};
};

/**
 * ATS + orderable for Choose your layout / Want It Sooner tile classification.
 * IM-004: when getProducts used `inventoryIds`, prefer the matching warehouse record
 * via {@link pickInventoryRecordForListIds}; ATS from that record first, then
 * {@link resolveEntityAts} (avoids top-level default ATS when plural records exist).
 *
 * @param {Object|null|undefined} product
 * @param {string|null|undefined} inventoryIdsFromCookie
 * @returns {{ ats: number|null, orderable: boolean|undefined }}
 */
const getLayoutInventoryFields = (product, inventoryIdsFromCookie) => {
  if (!product) {
    return {ats: null, orderable: undefined};
  }

  const inventory = pickInventoryRecordForListIds(product, inventoryIdsFromCookie) ?? product.inventory;
  const pickedAts = inventory?.ats ?? inventory?.stockLevel;

  return {
    ats: typeof pickedAts === 'number' ? pickedAts : resolveEntityAts(product, inventoryIdsFromCookie),
    orderable: product.orderable ?? inventory?.orderable,
  };
};

/**
 * Choose your layout tile: orderable MTO / layout master (DE-823).
 * Includes no inventory record, ats=0, and SFCC sentinel 999999 — not on-hand FG stock.
 *
 * @param {Object|null|undefined} product
 * @param {string|null|undefined} inventoryIdsFromCookie
 * @returns {boolean}
 */
const isChooseYourLayoutProduct = (product, inventoryIdsFromCookie) => {
  const {orderable, ats} = getLayoutInventoryFields(product, inventoryIdsFromCookie);

  return orderable === true && (ats == null || ats === 0 || ats === IN_STOCK_SENTINEL_ATS);
};

/**
 * Want It Sooner tile: real tracked on-hand stock only.
 * Excludes sentinel 999999 (same rule as deposit / payment-amount visibility).
 *
 * @param {Object|null|undefined} product
 * @param {string|null|undefined} inventoryIdsFromCookie
 * @returns {boolean}
 */
const isInStockProduct = (product, inventoryIdsFromCookie) => {
  const {orderable, ats} = getLayoutInventoryFields(product, inventoryIdsFromCookie);

  return orderable === true && ats != null && ats > 0 && ats !== IN_STOCK_SENTINEL_ATS;
};

/**
 * Normalize product for selector: use c_priceProcessed (SFRA) when present, else fallback to price/prices.
 * Ensures price, prices.{salePrice, listPrice, min, max}, priceType, priceMin, priceMax for tile.
 */
const withNormalizedPrice = (p) => {
  let salePrice;
  let listPrice;
  let min;
  let max;
  let priceType = 'single';

  const raw = p.c_priceProcessed;

  if (raw) {
    try {
      const processed = typeof raw === 'string' ? JSON.parse(raw) : raw;

      if (processed && typeof processed.salePrice === 'number') {
        salePrice = processed.salePrice;
        min = processed.min ?? salePrice;
        max = processed.max ?? salePrice;
        priceType = processed.type === 'range' ? 'range' : 'single';
        // listMin = min of list/retail pricebook (range products, set by SFRA hook)
        // listPrice = single product list price
        listPrice = processed.listMin ?? processed.listPrice ?? salePrice;
      }
    } catch {
      // Fall through to fallback
    }
  }

  if (salePrice == null) {
    salePrice = p.price ?? p.prices?.salePrice;
    listPrice = p.prices?.listPrice ?? salePrice;
    min = salePrice;
    max = salePrice;
  }

  // Fallback: derive listPrice from priceRanges (available via perPricebook: true in getProducts).
  // The retail/list range has a higher minPrice than the sale range — use it as strikethrough.
  if (listPrice == null || listPrice <= (salePrice ?? 0)) {
    const listRange = (p.priceRanges ?? []).find(
      (r) => typeof r.minPrice === 'number' && r.minPrice > (min ?? salePrice ?? 0)
    );

    if (listRange) listPrice = listRange.minPrice;
  }

  const prices = {...(p.prices || {}), salePrice, listPrice, min, max};

  return {
    ...p,
    price: salePrice,
    prices,
    priceType,
    priceMin: min,
    priceMax: max,
  };
};

/**
 * Configuration products for Choose Your Layout + Want It Sooner.
 *
 * Want It Sooner (`instockProducts`):
 * 1. Search the current PDP primary category → hydrate masters (expand=links)
 * 2. Collect `replacement` FG ids → hydrate FGs with warehouse inventory
 * 3. Tiles = in-stock FGs (ATS > 0)
 * 4. If that list is empty and `CAROUSEL_CATEGORY_FALLBACK`, use today’s
 *    category ATS > 0 hits until catalog automation is ready
 *
 * Flip `INVENTORY_POLICY.CAROUSEL_TILE_SUBJECT` to `'master'` later to render
 * masters that have at least one in-stock FG.
 *
 * Choose Your Layout (`configProducts`) still uses the category feed of
 * MTO-orderable products (ats=0, SFCC sentinel 999999, or no inventory record).
 *
 * Loading: `isLoading` / `isFetched` cover the full hook (layout + Want It Sooner).
 * `isLayoutFetched` / `isLayoutLoading` are layout-only — use for Choose your layout
 * trigger and choose-layout flyout so legacy category / FG fetches do not block UI.
 *
 * Legacy `CAROUSEL_SOURCE` values `replacement` / `category` remain for rollback.
 */
export const useConfigurationProducts = (product, _isComplexProduct, _hasConfigurableOptions = false) => {
  const shopperInventoryIds = useShopperInventoryIds();
  const carouselSource = INVENTORY_POLICY.CAROUSEL_SOURCE;
  const tileSubject = INVENTORY_POLICY.CAROUSEL_TILE_SUBJECT;
  const currentProductId = product?.id || product?.productId || null;

  const useRangeReplacementFeed = carouselSource === 'range-replacement';
  const useReplacementFeed = carouselSource === 'replacement';
  const useCategoryFeed = carouselSource === 'category';
  const useCategorySearch = useRangeReplacementFeed || useCategoryFeed;

  const categoryId = useCategorySearch ? product?.primaryCategoryId : undefined;

  const {
    hits: searchHits,
    total: searchTotal,
    isLoading: isSearchLoading,
    isComplete: isSearchComplete,
    error: searchError,
  } = usePaginatedProductSearch(categoryId);

  const searchComplete = !categoryId || isSearchComplete;

  const categoryProductIds = useCategorySearch && searchComplete ?
    searchHits.map((h) => h.productId || h.product_id).filter(Boolean) :
    [];

  const currentId = currentProductId ? String(currentProductId) : '';
  const rangeMasterIds = useRangeReplacementFeed && currentId && !categoryProductIds.includes(currentId) ?
    [...categoryProductIds, currentId] :
    categoryProductIds;

  const {
    data: rangeMastersResult,
    isLoading: isRangeMastersLoading,
    isFetched: isRangeMastersFetched,
    error: rangeMastersError,
  } = usePaginatedProducts(
    useRangeReplacementFeed && searchComplete ? rangeMasterIds : [],
    shopperInventoryIds,
    ['links']
  );
  const rangeMasters = rangeMastersResult?.data ?? [];

  const mastersWithCurrent = useMemo(
    () => (useRangeReplacementFeed ?
      mergeCurrentProductIntoMasters(rangeMasters, product) :
      rangeMasters),
    [useRangeReplacementFeed, rangeMasters, product]
  );

  const extraMasterIds = useMemo(() => {
    if (!useRangeReplacementFeed) return [];
    if (rangeMasterIds.length > 0 && !isRangeMastersFetched) return [];

    const fetchedIds = mastersWithCurrent.map((p) => p?.id || p?.productId).filter(Boolean);

    return collectUnresolvedMasterIds(mastersWithCurrent, fetchedIds);
  }, [useRangeReplacementFeed, rangeMasterIds.length, isRangeMastersFetched, mastersWithCurrent]);

  const {
    data: extraMastersResult,
    isLoading: isExtraMastersLoading,
    isFetched: isExtraMastersFetched,
    error: extraMastersError,
  } = usePaginatedProducts(
    useRangeReplacementFeed ? extraMasterIds : [],
    shopperInventoryIds,
    ['links']
  );
  const extraMasters = extraMastersResult?.data ?? [];

  const allMasters = useMemo(() => {
    if (!extraMasters.length) return mastersWithCurrent;

    const seen = new Set(mastersWithCurrent.map((p) => String(p?.id || p?.productId || '')));
    const merged = [...mastersWithCurrent];

    extraMasters.forEach((master) => {
      const id = String(master?.id || master?.productId || '');

      if (!id || seen.has(id)) return;

      seen.add(id);
      merged.push(master);
    });

    return merged;
  }, [mastersWithCurrent, extraMasters]);

  const extraMastersReady = extraMasterIds.length === 0 || isExtraMastersFetched;

  const rangeFinishedGoodIds = useMemo(
    () => (useRangeReplacementFeed && extraMastersReady ?
      collectReplacementFinishedGoodIdsFromProducts(allMasters) :
      []),
    [useRangeReplacementFeed, extraMastersReady, allMasters]
  );

  const replacementIds = useMemo(
    () => (useReplacementFeed ?
      collectReplacementFinishedGoodIdsFromProducts(product ? [product] : []) :
      []),
    [product, useReplacementFeed]
  );

  const finishedGoodIds = useRangeReplacementFeed ? rangeFinishedGoodIds : replacementIds;
  const finishedGoodIdsReady = useRangeReplacementFeed ? extraMastersReady : useReplacementFeed;

  const {
    data: finishedGoodProductsResult,
    isLoading: isFinishedGoodProductsLoading,
    isFetched: isFinishedGoodProductsFetched,
    error: finishedGoodProductsError,
  } = usePaginatedProducts(
    (useRangeReplacementFeed || useReplacementFeed) && finishedGoodIdsReady ? finishedGoodIds : [],
    shopperInventoryIds
  );

  const useCategoryFallback = Boolean(INVENTORY_POLICY.CAROUSEL_CATEGORY_FALLBACK);
  const fetchLegacyCategoryProducts =
    useCategoryFeed || (useRangeReplacementFeed && useCategoryFallback);

  const {
    data: legacyCategoryProductsResult,
    isLoading: isLegacyCategoryProductsLoading,
    isFetched: isLegacyCategoryProductsFetched,
    error: legacyCategoryProductsError,
  } = usePaginatedProducts(
    fetchLegacyCategoryProducts && searchComplete ? categoryProductIds : [],
    shopperInventoryIds
  );

  const layoutSourceProducts = useRangeReplacementFeed ?
    rangeMasters :
    (useCategoryFeed ? (legacyCategoryProductsResult?.data ?? []) : []);

  const finishedGoodProducts = finishedGoodProductsResult?.data ?? [];
  const keepAtsFilter = INVENTORY_POLICY.CAROUSEL_FILTER_ATS_GT_ZERO;
  // Shared Want It Sooner matcher — warehouse-aware via shopperInventoryIds on each product.
  const isInStockWantItSoonerProduct = useCallback(
    (candidateProduct) => candidateProduct && (!keepAtsFilter || isInStockProduct(candidateProduct, shopperInventoryIds)),
    [keepAtsFilter, shopperInventoryIds]
  );

  const rangeFeedReady = !useRangeReplacementFeed || (
    extraMastersReady && (finishedGoodIds.length === 0 || isFinishedGoodProductsFetched)
  );

  const rangeInstockFinishedGoods = useMemo(() => {
    if (!(useRangeReplacementFeed || useReplacementFeed)) return [];

    return finishedGoodProducts.filter(isInStockWantItSoonerProduct);
  }, [useRangeReplacementFeed, useReplacementFeed, finishedGoodProducts, isInStockWantItSoonerProduct]);

  const rangeTiles = selectWantItSoonerTiles({
    tileSubject: (useRangeReplacementFeed || useReplacementFeed) ? tileSubject : 'finished-good',
    masters: allMasters,
    instockFinishedGoods: rangeInstockFinishedGoods,
    currentProductId,
  });

  const useLegacyTiles = useCategoryFeed || (
    useRangeReplacementFeed && useCategoryFallback && rangeFeedReady && rangeTiles.length === 0
  );

  const legacyInstockProducts = useMemo(() => {
    if (!useLegacyTiles) return [];

    return (legacyCategoryProductsResult?.data ?? []).filter((product) => {
      const productId = String(product?.id || product?.productId || '');

      return productId && productId !== currentId && isInStockWantItSoonerProduct(product);
    });
  }, [useLegacyTiles, legacyCategoryProductsResult, currentId, isInStockWantItSoonerProduct]);

  const instockProducts = (useLegacyTiles ? legacyInstockProducts : rangeTiles)
    .map((p) => withNormalizedPrice(p));

  const instockProductIds = new Set(
    instockProducts.map((p) => p.id || p.productId)
  );

  const configProducts = (useRangeReplacementFeed || useCategoryFeed) ?
    layoutSourceProducts
      // Layout masters only — drop in-stock FGs already on Want It Sooner.
      .filter((p) => isChooseYourLayoutProduct(p, shopperInventoryIds) &&
        !instockProductIds.has(p.id || p.productId))
      .map((p) => withNormalizedPrice({...p, displayTitle: p.name ?? ''})) :
    [];

  const error = searchError ??
    rangeMastersError ??
    extraMastersError ??
    finishedGoodProductsError ??
    legacyCategoryProductsError ??
    null;

  const isLoading = (useCategorySearch && isSearchLoading) ||
    isRangeMastersLoading ||
    isExtraMastersLoading ||
    isFinishedGoodProductsLoading ||
    ((useCategoryFeed || useLegacyTiles) && isLegacyCategoryProductsLoading);

  const isFetched = useRangeReplacementFeed ?
    ((rangeMasterIds.length === 0 || isRangeMastersFetched) && extraMastersReady &&
      (finishedGoodIds.length === 0 || isFinishedGoodProductsFetched) &&
      (!useLegacyTiles || categoryProductIds.length === 0 || isLegacyCategoryProductsFetched)) :
    (useReplacementFeed ?
      (isFinishedGoodProductsFetched || replacementIds.length === 0) :
      (useCategoryFeed ? isLegacyCategoryProductsFetched : true));

  // Layout list ready for Choose your layout — ignores Want It Sooner / legacy category fallback fetches.
  const isLayoutFetched = useCategoryFeed ?
    isLegacyCategoryProductsFetched :
    useRangeReplacementFeed ?
      (searchComplete &&
        (rangeMasterIds.length === 0 || isRangeMastersFetched) &&
        extraMastersReady) :
      true;
  // Layout-only loading — do not derive from full-hook isLoading (Want It Sooner may still be in flight).
  const isLayoutLoading = (useRangeReplacementFeed || useCategoryFeed) && !isLayoutFetched;

  return {
    configProducts,
    instockProducts,
    isLoading,
    isFetched,
    isLayoutLoading,
    isLayoutFetched,
    total: useRangeReplacementFeed || useReplacementFeed ?
      instockProducts.length :
      (searchTotal ?? 0),
    isError: Boolean(error),
    error,
  };
};
